import {
  QueryTableProps,
  useRawTableRows,
} from "@/components/query-table/QueryTable.shared";
import { useSelectedRowIds } from "@/components/query-table/hooks/useSelectedRowIds";
import { useSelectedRowSnapshotsStore } from "@/components/query-table/stores/selectedRowSnapshotsStore";
import {
  DataTableFeatures,
  DataTableSettings,
} from "@/types/dataTableSettings";
import { ColumnDef } from "@tanstack/react-table";
import { useCallback, useLayoutEffect, useMemo } from "react";
import DataTableColumnOrderContext from "../../data-table/components/DataTableColumnOrderContext";
import DataTableColumnPinningContext from "../../data-table/components/DataTableColumnPinningContext";
import DataTableColumnSizingContext from "../../data-table/components/DataTableColumnSizingContext";
import DataTableColumnVisibilityContext from "../../data-table/components/DataTableColumnVisibilityContext";
import { DataTableSettingsContextProvider } from "../../data-table/DataTableSettingsContext";
import useColumns from "../../data-table/hooks/useColumns";
import useTable from "../../data-table/hooks/useTable";

interface QueryTableApiViewProps<T extends { id: string }> {
  listProps: QueryTableProps<T>;
  tableFeaturesMerged: DataTableFeatures;
}

export function QueryTableApiView<T extends { id: string }>({
  listProps,
  tableFeaturesMerged,
}: QueryTableApiViewProps<T>) {
  const {
    name,
    schema,
    columnOverrides,
    children,
    translationKey,
    query,
    options,
    dataMapper,
    pathBuilder,
    rowSelection,
    setRowSelection,
  } = listProps;
  const selectedIds = useSelectedRowIds({ rowSelection });

  const rawTableRows = useRawTableRows(query);

  useLayoutEffect(() => {
    if (
      !tableFeaturesMerged.enableOnlySelectedFilter ||
      !tableFeaturesMerged.enableRowSelection
    ) {
      return;
    }
    useSelectedRowSnapshotsStore
      .getState()
      .syncFromVisibleRows(name, rawTableRows, selectedIds);
  }, [
    tableFeaturesMerged.enableOnlySelectedFilter,
    tableFeaturesMerged.enableRowSelection,
    rawTableRows,
    selectedIds,
    name,
  ]);

  const tableData = useMemo(
    () => (dataMapper ? dataMapper(rawTableRows ?? []) : rawTableRows),
    [rawTableRows, dataMapper],
  );

  const columns = useColumns({
    name,
    translationKey,
    schema,
    overrides: columnOverrides,
    enableRowSelection: tableFeaturesMerged.enableRowSelection,
    enableSelectAll: tableFeaturesMerged.enableSelectAll,
  });

  const meta = useMemo(() => ({ pathBuilder }), [pathBuilder]);
  const getRowId = useCallback((row: T) => row.id, []);

  const {
    table,
    stateColumnVisibility,
    stateColumnSizing,
    stateColumnOrder,
    stateColumnPinning,
    initialColumnVisibility,
  } = useTable({
    name,
    data: tableData,
    enableSorting: tableFeaturesMerged.enableSorting,
    enableColumnPinning: tableFeaturesMerged.enableColumnPinning,
    enableFilters: tableFeaturesMerged.enableFilters,
    enableGrouping: tableFeaturesMerged.enableGrouping,
    groupBy: tableFeaturesMerged.groupBy,
    enableRowSelection: tableFeaturesMerged.enableRowSelection,
    meta,
    columns: columns as ColumnDef<unknown, number>[],
    initialColumnSizes: tableFeaturesMerged.initialColumnSizes,
    getRowId,
    initialColumnVisibility: tableFeaturesMerged.initialColumnVisibility,
    getRowProps: tableFeaturesMerged.getRowProps,
    ...(rowSelection !== undefined && setRowSelection !== undefined
      ? { rowSelection, setRowSelection }
      : {}),
  });

  const rowsCount = table.getRowCount();

  const visibleHeaders = table.getLeafHeaders();
  const firstVisibleColumnId = visibleHeaders.find(
    (header) =>
      header.column.id !== "select" &&
      header.column.columnDef.meta?.allowColumnToBeALink !== false,
  )?.column.id;

  const contextValue = useMemo(
    () =>
      ({
        name,
        features: tableFeaturesMerged,
        options,
        table,
        isResizing: !!table.getState().columnSizingInfo.isResizingColumn,
        firstVisibleColumnId,
        rowsCount,
      }) as DataTableSettings<T>,
    [
      name,
      tableFeaturesMerged,
      options,
      table,
      firstVisibleColumnId,
      rowsCount,
    ],
  );

  const columnSizing = useMemo(
    () => ({ columnSizing: stateColumnSizing }),
    [stateColumnSizing],
  );
  const columnOrder = useMemo(
    () => ({ columnOrder: stateColumnOrder }),
    [stateColumnOrder],
  );
  const columnPinning = useMemo(
    () => ({ columnPinning: stateColumnPinning }),
    [stateColumnPinning],
  );
  const columnVisibility = useMemo(
    () => ({
      columnVisibility: stateColumnVisibility,
      resetColumnVisibility: () => {
        table.setColumnVisibility(initialColumnVisibility);
      },
    }),
    [stateColumnVisibility, table, initialColumnVisibility],
  );

  return (
    <DataTableColumnSizingContext.Provider value={columnSizing}>
      <DataTableColumnOrderContext.Provider value={columnOrder}>
        <DataTableColumnPinningContext.Provider value={columnPinning}>
          <DataTableColumnVisibilityContext.Provider value={columnVisibility}>
            <DataTableSettingsContextProvider value={contextValue}>
              {children}
            </DataTableSettingsContextProvider>
          </DataTableColumnVisibilityContext.Provider>
        </DataTableColumnPinningContext.Provider>
      </DataTableColumnOrderContext.Provider>
    </DataTableColumnSizingContext.Provider>
  );
}
